import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Form, Radio, Input, Alert, Space, Steps, Spin } from 'antd';
import { CheckCircleOutlined, LockOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';

const { Title, Text } = Typography;
const { Step } = Steps;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface Customer {
  id: number;
  name: string;
  vehicleCount: number;
  lastServiceDate?: string;
}

interface VerificationQuestion {
  id: string;
  type: 'date' | 'amount' | 'multiple_choice' | 'vehicle';
  question: string;
  options?: string[];
}

interface LinkingRequest {
  id: number;
  phone: string;
  customer_id: number;
  questions: VerificationQuestion[];
  expires_at: string;
  attempts: number;
  max_attempts: number;
}

interface AccountLinkingProps {
  phone: string;
  customers: Customer[];
  onSuccess?: () => void;
  onCancel?: () => void;
}

const AccountLinking: React.FC<AccountLinkingProps> = ({
  phone,
  customers,
  onSuccess,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const { register } = useCustomerAuth();
  const [step, setStep] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [linkingRequest, setLinkingRequest] = useState<LinkingRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCustomerSelect = async () => {
    if (!selectedCustomer) {
      setError('Please select a customer record');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Initiate account linking
      const response = await axios.post(`${API_URL}/customer-auth/initiate-linking`, {
        phone,
        customerId: selectedCustomer,
      });

      setLinkingRequest(response.data);
      setStep(1);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to initiate account linking');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (values: any) => {
    if (!linkingRequest) return;

    setLoading(true);
    setError(null);

    try {
      // Build answers array
      const answers = linkingRequest.questions.map((q) => ({
        questionId: q.id,
        answer: values[q.id],
      }));

      // Verify and link account
      const response = await axios.post(`${API_URL}/customer-auth/verify-linking`, {
        requestId: linkingRequest.id,
        answers,
        password: values.password,
      });

      // Account linked successfully - tokens are in response
      const { accessToken, refreshToken } = response.data;

      // Store tokens
      localStorage.setItem('customer_access_token', accessToken);
      localStorage.setItem('customer_refresh_token', refreshToken);

      setStep(2);

      // Call onSuccess after a short delay
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      style={{
        maxWidth: 600,
        margin: '0 auto',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ textAlign: 'center' }}>
          <Title level={3}>Link Your Account</Title>
          <Text type="secondary">
            We found existing records for your phone number. Verify your identity to link them.
          </Text>
        </div>

        <Steps current={step} size="small">
          <Step title="Select Record" />
          <Step title="Verify Identity" />
          <Step title="Complete" />
        </Steps>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}

        {/* Step 1: Select Customer */}
        {step === 0 && (
          <div>
            <Text strong>Select your customer record:</Text>
            <Radio.Group
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              style={{ width: '100%', marginTop: 16 }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {customers.map((customer) => (
                  <Radio key={customer.id} value={customer.id} style={{ width: '100%' }}>
                    <Card size="small" style={{ marginLeft: 24 }}>
                      <div>
                        <Text strong>{customer.name}</Text>
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {customer.vehicleCount} vehicle(s)
                        </Text>
                        {customer.lastServiceDate && (
                          <Text type="secondary" style={{ fontSize: 12, marginLeft: 16 }}>
                            Last service: {new Date(customer.lastServiceDate).toLocaleDateString()}
                          </Text>
                        )}
                      </div>
                    </Card>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>

            <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
              <Button
                type="primary"
                onClick={handleCustomerSelect}
                loading={loading}
                disabled={!selectedCustomer}
                block
              >
                Continue
              </Button>
              {onCancel && (
                <Button onClick={onCancel} block>
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Verification Questions */}
        {step === 1 && linkingRequest && (
          <div>
            <Alert
              message="Identity Verification"
              description={`Please answer the following questions to verify your identity. You have ${
                linkingRequest.max_attempts - linkingRequest.attempts
              } attempt(s) remaining.`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form form={form} layout="vertical" onFinish={handleVerification}>
              {linkingRequest.questions.map((question, index) => (
                <Form.Item
                  key={question.id}
                  label={`${index + 1}. ${question.question}`}
                  name={question.id}
                  rules={[{ required: true, message: 'This field is required' }]}
                >
                  {question.options ? (
                    <Radio.Group>
                      <Space direction="vertical">
                        {question.options.map((option) => (
                          <Radio key={option} value={option}>
                            {option}
                          </Radio>
                        ))}
                      </Space>
                    </Radio.Group>
                  ) : (
                    <Input placeholder="Enter your answer" />
                  )}
                </Form.Item>
              ))}

              <Form.Item
                label="Create Password"
                name="password"
                rules={[
                  { required: true, message: 'Please create a password' },
                  { min: 8, message: 'Password must be at least 8 characters' },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="At least 8 characters"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                label="Confirm Password"
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm your password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Re-enter password"
                  size="large"
                />
              </Form.Item>

              <Form.Item>
                <Space style={{ width: '100%' }}>
                  <Button type="primary" htmlType="submit" loading={loading} block>
                    Verify & Link Account
                  </Button>
                  {onCancel && (
                    <Button onClick={onCancel} block>
                      Cancel
                    </Button>
                  )}
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 2 && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
            <Title level={3} style={{ marginTop: 24 }}>
              Account Linked Successfully!
            </Title>
            <Text type="secondary">
              Your account has been linked. Redirecting...
            </Text>
            <Spin style={{ marginTop: 24 }} />
          </div>
        )}
      </Space>
    </Card>
  );
};

export default AccountLinking;
